import { useEffect, useState } from "react";
import api from "../../api/api";
import WebSocketService from "../../services/WebSocketService";

import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from "recharts";

export default function DashboardStats() {
    const [stats, setStats] = useState({
        shipments: 0,
        activeOrders: 0,
        iotDevices: 0,
        onlineDevices: 0,
        blockchainLogs: 0,
        apiStatus: "Unknown",
        lastBlockchainEvent: "�",
    });

    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [liveEvents, setLiveEvents] = useState([]);

    useEffect(() => {
        let timer;

        const loadStats = async () => {
            setLoading(true);

            try {
                const [shipmentsRes, ordersRes, iotRes, blockchainRes] =
                    await Promise.allSettled([
                        api.get("/Shipment/all"),
                        api.get("/orders/active"),
                        api.get("/IoTDevice"),
                        api.get("/BlockchainLog"),
                    ]);

                const shipments =
                    shipmentsRes.status === "fulfilled" && Array.isArray(shipmentsRes.value.data)
                        ? shipmentsRes.value.data
                        : [];

                const orders =
                    ordersRes.status === "fulfilled" && Array.isArray(ordersRes.value.data)
                        ? ordersRes.value.data
                        : [];

                const devices =
                    iotRes.status === "fulfilled" && Array.isArray(iotRes.value.data)
                        ? iotRes.value.data
                        : [];

                const logs =
                    blockchainRes.status === "fulfilled" && Array.isArray(blockchainRes.value.data)
                        ? blockchainRes.value.data
                        : [];

                const lastBlockchainEvent =
                    logs.length > 0
                        ? logs[0]?.createdAt || logs[0]?.timestamp || logs[0]?.date || "Available"
                        : "No events";

                const onlineDevices = devices.filter((d) => {
                    if (!d.lastUpdated) return false;
                    return Date.now() - new Date(d.lastUpdated).getTime() < 2 * 60 * 1000;
                }).length;

                setStats({
                    shipments: shipments.length,
                    activeOrders: orders.length,
                    iotDevices: devices.length,
                    onlineDevices,
                    blockchainLogs: logs.length,
                    apiStatus: "Online",
                    lastBlockchainEvent,
                });
            } finally {
                setLastUpdated(new Date());
                setLoading(false);
            }
        };

        loadStats();
        timer = setInterval(loadStats, 15000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const ws = new WebSocketService();

        ws.onMessage = (raw) => {
            try {
                const msg = JSON.parse(raw);

                setLiveEvents((prev) =>
                    [
                        {
                            id: `${Date.now()}-${Math.random()}`,
                            type: msg.type || "event",
                            message:
                                msg.message ||
                                msg.trackingNumber ||
                                "Live update received",
                            time: new Date().toLocaleTimeString(),
                        },
                        ...prev,
                    ].slice(0, 8)
                );

                if (msg.type === "iot_update") {
                    setStats((prev) => ({
                        ...prev,
                        apiStatus: "Online",
                    }));
                    setLastUpdated(new Date());
                }

                if (msg.type === "blockchain-log-created") {
                    setStats((prev) => ({
                        ...prev,
                        blockchainLogs: prev.blockchainLogs + 1,
                        lastBlockchainEvent: new Date().toLocaleString(),
                    }));
                    setLastUpdated(new Date());
                }
            } catch (e) {
                console.error("Dashboard WS error", e);
            }
        };

        ws.connect();

        return () => {
            if (typeof ws.close === "function") {
                ws.close();
            } else if (typeof ws.disconnect === "function") {
                ws.disconnect();
            }
        };
    }, []);

    const card = (title, value, subtitle, color) => (
        <div
            style={{
                background: color,
                color: "white",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 6px 16px rgba(0,0,0,.15)",
            }}
        >
            <div style={{ fontSize: "14px", opacity: 0.9 }}>{title}</div>
            <div style={{ fontSize: "34px", fontWeight: "bold", marginTop: "12px" }}>
                {loading ? "..." : value}
            </div>
            <div style={{ marginTop: "10px", fontSize: "13px" }}>{subtitle}</div>
        </div>
    );

    const barData = [
        { name: "Shipments", value: stats.shipments },
        { name: "Orders", value: stats.activeOrders },
        { name: "Blockchain", value: stats.blockchainLogs },
    ];

    const pieData = [
        { name: "Online", value: stats.onlineDevices },
        { name: "Offline", value: Math.max(stats.iotDevices - stats.onlineDevices, 0) },
    ];

    return (
        <div style={{ marginBottom: "24px" }}>
            <h2 style={{ marginBottom: "16px" }}>Operational Dashboard</h2>

            <p style={{ color: "#666", marginBottom: "16px" }}>
                Live refresh every 15 seconds
                {lastUpdated && <> � Last updated: {lastUpdated.toLocaleTimeString()}</>}
            </p>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "16px",
                    marginBottom: "24px",
                }}
            >
                {card("Shipments", stats.shipments, "Total Shipments", "#1976D2")}
                {card("Orders", stats.activeOrders, "Active Orders", "#43A047")}
                {card("IoT Devices", stats.iotDevices, "Registered Devices", "#7B1FA2")}
                {card("Online", stats.onlineDevices, "Currently Online", "#FB8C00")}
                {card("Blockchain", stats.blockchainLogs, "Audit Events", "#546E7A")}
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                    gap: "20px",
                }}
            >
                <div style={chartBoxStyle}>
                    <h3>Operational Activity</h3>
                    <div style={{ height: 260 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={barData}>
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="value" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div style={chartBoxStyle}>
                    <h3>IoT Device Status</h3>
                    <div style={{ height: 260 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    nameKey="name"
                                    outerRadius={90}
                                    label
                                >
                                    <Cell />
                                    <Cell />
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div style={chartBoxStyle}>
                    <h3>System Health</h3>
                    <div style={{ display: "grid", gap: "10px", fontSize: "15px" }}>
                        <div>
                            <b>API:</b>{" "}
                            <span style={{ color: stats.apiStatus === "Online" ? "green" : "red" }}>
                                {stats.apiStatus}
                            </span>
                        </div>
                        <div>
                            <b>IoT Devices:</b> {stats.onlineDevices} / {stats.iotDevices} online
                        </div>
                        <div>
                            <b>Blockchain Events:</b> {stats.blockchainLogs}
                        </div>
                        <div>
                            <b>Last Blockchain Event:</b> {stats.lastBlockchainEvent}
                        </div>
                        <div>
                            <b>Refresh:</b> Every 15 seconds
                        </div>
                    </div>
                </div>

                <div style={chartBoxStyle}>
                    <h3>Live Events</h3>

                    {liveEvents.length === 0 ? (
                        <p style={{ color: "#777" }}>Waiting for live events...</p>
                    ) : (
                        <div style={{ display: "grid", gap: "8px" }}>
                            {liveEvents.map((e) => (
                                <div
                                    key={e.id}
                                    style={{
                                        borderBottom: "1px solid #eee",
                                        paddingBottom: "6px",
                                        fontSize: "14px",
                                    }}
                                >
                                    <b>{e.type}</b>
                                    <br />
                                    {e.message}
                                    <br />
                                    <span style={{ color: "#777", fontSize: "12px" }}>
                                        {e.time}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const chartBoxStyle = {
    background: "white",
    borderRadius: "12px",
    padding: "18px",
    boxShadow: "0 4px 12px rgba(0,0,0,.08)",
};